import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  FileCheck2, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Info, Fingerprint, Hash, Sparkles, ChevronRight, Undo2,
} from 'lucide-react';


const SEVERITY_TONE = {
  critical: 'bg-rose-50 text-rose-800 border-rose-300',
  high:     'bg-amber-50 text-amber-800 border-amber-300',
  medium:   'bg-amber-50 text-amber-700 border-amber-200',
  low:      'bg-sky-50 text-sky-700 border-sky-200',
  info:     'bg-slate-50 text-slate-600 border-slate-200',
};


function CheckRow({ check, testid }) {
  const Icon = check.ok ? CheckCircle2 : (
    check.severity === 'critical' ? XCircle
      : check.severity === 'info' ? Info : AlertTriangle
  );
  const tone = check.ok
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : (SEVERITY_TONE[check.severity] || SEVERITY_TONE.info);
  return (
    <li className={`flex items-start gap-2 rounded-md border px-2 py-1 text-[10.5px] ${tone}`}
        data-testid={testid}>
      <Icon className="w-3 h-3 mt-0.5 shrink-0" />
      <span className="leading-relaxed">{check.message_ar}</span>
    </li>
  );
}


export default function JournalPreviewPanel({ billId, onPosted }) {
  const [preview, setPreview] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reverseMode, setReverseMode] = useState(false);
  const [reverseReason, setReverseReason] = useState('');

  const refresh = useCallback(async () => {
    if (!preview?.id) return;
    try {
      const [pv, val] = await Promise.all([
        axiosInstance.get(`/bill-review/journal-preview/${preview.id}`),
        axiosInstance.get(`/bill-review/journal-preview/${preview.id}/pre-post-check`),
      ]);
      setPreview(pv.data);
      setValidation(val.data);
    } catch { /* ignore */ }
  }, [preview?.id]);

  // Auto-load latest preview on mount (so posted bills re-open with
  // their posted badge and reversal controls, without asking the user
  // to click Build again).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/bill-review/${billId}/latest-preview`);
        if (cancelled || !data.preview) return;
        setPreview(data.preview);
        const { data: val } = await axiosInstance.get(
          `/bill-review/journal-preview/${data.preview.id}/pre-post-check`);
        if (!cancelled) setValidation(val);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [billId]);

  const build = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `/bill-review/${billId}/journal-preview`);
      if (data.error) {
        toast.error(data.message || data.error);
        return;
      }
      setPreview(data.preview);
      toast.success(data.reused
        ? `تَم استِرجاع المُعايَنَة (${data.preview.preview_id})`
        : `تَم بِناء المُعايَنَة (${data.preview.preview_id})`);
      // Fetch validation.
      const { data: val } = await axiosInstance.get(
        `/bill-review/journal-preview/${data.preview.id}/pre-post-check`);
      setValidation(val);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل بِناء المُعايَنَة');
    } finally { setLoading(false); }
  };

  const reverse = async () => {
    if (!preview?.journal_id) return;
    if (!reverseReason || reverseReason.trim().length < 5) {
      toast.error('يَجِب إدخال سَبَب واضِح (5 حُروف على الأَقَل)');
      return;
    }
    setReversing(true);
    try {
      const { data } = await axiosInstance.post(
        `/bill-review/journal/${preview.journal_id}/reverse`,
        { reason: reverseReason });
      if (data.error) {
        toast.error(data.message || data.error);
      } else {
        toast.success(`تَم عَكس القَيد — ${data.reversal_journal_no}`);
        onPosted?.(data);
      }
      setReverseMode(false); setReverseReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل العَكس');
    } finally { setReversing(false); }
  };

  const post = async () => {
    if (!preview) return;
    setPosting(true);
    try {
      const { data } = await axiosInstance.post(
        `/bill-review/journal-preview/${preview.id}/post`);
      if (data.ok) {
        toast.success(`تَم التَّرحيل — ${data.journal_no}`);
        // Refresh both preview + validation so UI reflects new
        // posted state (JV number displayed on the badge).
        const [pv, val] = await Promise.all([
          axiosInstance.get(`/bill-review/journal-preview/${preview.id}`),
          axiosInstance.get(`/bill-review/journal-preview/${preview.id}/pre-post-check`),
        ]);
        setPreview(pv.data);
        setValidation(val.data);
        onPosted?.(data);
      } else {
        toast.error('فَشَلَت البَوّابَة النِّهائِيَّة — راجِع القائِمَة');
        setValidation(data.validation);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّرحيل');
    } finally { setPosting(false); }
  };

  // Empty state — no preview built yet
  if (!preview) {
    return (
      <div className="rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/40 p-4"
           data-testid="journal-preview-empty">
        <div className="flex items-center gap-2 text-sm font-bold text-indigo-900 mb-1">
          <FileCheck2 className="w-4 h-4" /> مُعايَنَة القَيد المُحاسَبي
        </div>
        <p className="text-[11px] text-indigo-800 mb-3 leading-relaxed">
          بَعد نَجاح التَّحَقُّق، اضغَط لِبِناء مُعايَنَة القَيد.
          <strong className="block mt-1">
            لَن يَتِم إنشاء رَقم قَيد رَسمي (JV) قَبل الضَّغط على &quot;تَرحيل&quot;.
          </strong>
        </p>
        <button
          onClick={build} disabled={loading}
          data-testid="jp-build-btn"
          className="w-full text-xs font-semibold rounded-lg bg-indigo-600
                     hover:bg-indigo-700 text-white px-3 py-2 inline-flex
                     items-center justify-center gap-1 disabled:opacity-50"
        >
          {loading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <FileCheck2 className="w-3.5 h-3.5" />}
          بِناء مُعايَنَة القَيد
        </button>
      </div>
    );
  }

  const isStale   = preview.status === 'stale';
  const isPosted  = preview.status === 'posted';
  const isFresh   = preview.freshness?.fresh !== false;
  const canPost   = validation?.can_post && !isStale && !isPosted;

  return (
    <div className="rounded-xl border-2 border-indigo-200 bg-white p-3 space-y-3"
         data-testid="journal-preview-panel">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <FileCheck2 className="w-4 h-4 text-indigo-600" />
          <span className="text-[11px] font-bold text-indigo-900">
            مُعايَنَة القَيد
          </span>
          <span className="text-[10px] font-mono bg-indigo-100 text-indigo-800 rounded px-1.5 py-0.5"
                data-testid="jp-preview-id">
            {preview.preview_id}
          </span>
        </div>
        <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${
          isPosted ? 'bg-emerald-100 text-emerald-800'
            : isStale ? 'bg-amber-100 text-amber-800'
              : 'bg-slate-100 text-slate-700'
        }`} data-testid="jp-preview-status">
          {isPosted ? 'مُرَحَّل' : isStale ? 'قَديم' : 'نَشِط'}
        </span>
      </div>

      {isPosted && preview.journal_no && (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-2 text-center"
             data-testid="jp-posted-badge">
          <CheckCircle2 className="w-4 h-4 mx-auto text-emerald-600 mb-1" />
          <div className="text-[11px] font-semibold text-emerald-900">
            تَم التَّرحيل — القَيد
          </div>
          <div className="text-sm font-mono font-bold text-emerald-800 mt-0.5"
               data-testid="jp-jv-number">
            {preview.journal_no}
          </div>
          {/* Reversal controls */}
          {!reverseMode ? (
            <button
              onClick={() => setReverseMode(true)}
              data-testid="jp-reverse-mode"
              className="mt-2 text-[10.5px] font-semibold text-rose-700 hover:text-rose-900
                         inline-flex items-center gap-1"
            >
              <Undo2 className="w-3 h-3" /> عَكس القَيد
            </button>
          ) : (
            <div className="mt-2 space-y-1.5 text-right">
              <textarea
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                data-testid="jp-reverse-reason"
                placeholder="سَبَب العَكس (إلزامي — 5 حُروف على الأَقَل)"
                className="w-full rounded border border-rose-300 px-2 py-1
                           text-[11px] h-14 resize-none"
              />
              <div className="flex gap-1">
                <button
                  onClick={reverse} disabled={reversing || reverseReason.trim().length < 5}
                  data-testid="jp-reverse-confirm"
                  className="flex-1 text-[11px] font-semibold rounded bg-rose-600
                             hover:bg-rose-700 text-white px-2 py-1 inline-flex
                             items-center justify-center gap-1 disabled:opacity-40"
                >
                  {reversing
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Undo2 className="w-3 h-3" />}
                  تَأكيد العَكس
                </button>
                <button
                  onClick={() => { setReverseMode(false); setReverseReason(''); }}
                  data-testid="jp-reverse-cancel"
                  className="text-[11px] rounded border border-slate-300 px-2 py-1"
                >
                  إلغاء
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Freshness banner when stale */}
      {isStale && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 p-2 text-[11px] text-amber-900"
             data-testid="jp-stale-banner">
          <div className="flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-3 h-3" />
            المُعايَنَة قَديمَة — تَغَيَّرَت الفاتورَة
          </div>
          <div className="mt-1">أَعِد بِناء المُعايَنَة قَبل التَّرحيل.</div>
          <button
            onClick={build} disabled={loading}
            data-testid="jp-rebuild-btn"
            className="mt-2 text-[10.5px] font-semibold rounded bg-amber-600
                       hover:bg-amber-700 text-white px-2 py-1"
          >
            إعادَة بِناء
          </button>
        </div>
      )}

      {/* Lines table */}
      <div>
        <div className="text-[10px] font-semibold text-slate-500 mb-1">
          سُطور القَيد ({preview.lines?.length || 0})
        </div>
        <table className="w-full text-[10.5px]">
          <thead>
            <tr className="border-b text-slate-500">
              <th className="text-right py-1 px-1">الحِساب</th>
              <th className="text-left py-1 px-1">مَدين</th>
              <th className="text-left py-1 px-1">دائِن</th>
            </tr>
          </thead>
          <tbody>
            {(preview.lines || []).map((l, i) => (
              <tr key={i} className="border-b last:border-b-0"
                  data-testid={`jp-line-${i}`}>
                <td className="py-1 px-1">
                  <div className="font-mono text-[10px] text-slate-500">
                    {l.account_code}
                  </div>
                  <div className="text-[10.5px]">{l.account_name}</div>
                  {(l.cost_center_id || l.project_id) && (
                    <div className="text-[9px] text-slate-400 font-mono">
                      {l.cost_center_id && `CC:${l.cost_center_id}`}
                      {l.project_id && ` PJ:${l.project_id}`}
                    </div>
                  )}
                </td>
                <td className="text-left py-1 px-1 font-mono">
                  {l.debit ? Number(l.debit).toLocaleString('en') : ''}
                </td>
                <td className="text-left py-1 px-1 font-mono">
                  {l.credit ? Number(l.credit).toLocaleString('en') : ''}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-700 font-bold">
              <td className="py-1 px-1">الإجمالي</td>
              <td className="text-left py-1 px-1 font-mono text-emerald-700"
                  data-testid="jp-total-debit">
                {Number(preview.total_debit || 0).toLocaleString('en')}
              </td>
              <td className="text-left py-1 px-1 font-mono text-emerald-700"
                  data-testid="jp-total-credit">
                {Number(preview.total_credit || 0).toLocaleString('en')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Fingerprint */}
      <details className="text-[10px]" data-testid="jp-fingerprint">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700
                             font-semibold inline-flex items-center gap-1">
          <Fingerprint className="w-3 h-3" /> البَصمَة (SHA256)
        </summary>
        <div className="mt-1 font-mono text-[9px] text-slate-600 break-all
                        bg-slate-50 rounded p-1.5">
          {preview.fingerprint}
        </div>
        {!isFresh && (
          <div className="mt-1 text-rose-700 font-semibold">
            ⚠ البَصمَة الحالِيَّة: {preview.freshness?.current_fingerprint?.slice(0, 16)}…
          </div>
        )}
      </details>

      {/* Pre-posting validation */}
      {validation && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold text-slate-500">
              بَوّابَة التَّرحيل النِّهائِيَّة
            </span>
            <span className="text-[10px] font-mono text-slate-500"
                   data-testid="jp-validation-counter">
              {(validation.checks || []).filter((c) => c.ok).length}/
              {(validation.checks || []).length} ✓
            </span>
          </div>
          <ul className="space-y-1">
            {(validation.checks || []).map((c, i) => (
              <CheckRow key={i} check={c}
                         testid={`jp-check-${c.key}`} />
            ))}
          </ul>
        </div>
      )}

      {/* Post button */}
      {!isPosted && (
        <button
          onClick={post} disabled={posting || !canPost}
          data-testid="jp-post-btn"
          className="w-full text-sm font-bold rounded-lg bg-emerald-600
                     hover:bg-emerald-700 text-white px-3 py-2.5 inline-flex
                     items-center justify-center gap-1.5 disabled:opacity-40
                     disabled:cursor-not-allowed"
        >
          {posting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <ChevronRight className="w-4 h-4" />}
          تَرحيل القَيد (allocate JV)
        </button>
      )}
    </div>
  );
}
